<template>
    <div class="segment-container" :class="[variant]">
        <div class="segment" :class="[variant, { active, hover }]">
            <div v-if="slots.icon" class="segment-icon">
                <slot name="icon" />
            </div>
            <div class="segment-text">
                <slot />
            </div>
            <div class="notch" />
        </div>
    </div>
</template>

<script setup>
import { useSlots } from 'vue';
import { ComponentVariant } from 'ihunter-pathfinder-components';

const slots = useSlots();

const props = defineProps({
    modelValue: {
        type: [String, Number, null],
        required: true,
    },
    variant: {
        type: String,
        default: ComponentVariant.Default,
    },
    active: {
        type: Boolean,
        default: false,
    },
    hover: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.segment-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: max-content;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: transparent;
    user-select: none;
    align-items: center;
    gap: Pathfinder.$space-100;

    &.thumbnail {
        overflow: visible;
    }
}

.segment {
    @include Pathfinder.Row-Macro;
    color: var(--on-surface);
    border: none;
    display: flex;
    width: 100%;
    align-items: center;
    padding: Pathfinder.$space-150 0;
    border-radius: Pathfinder.$space-50;
    background: var(--surface);
    border: 1px solid transparent;
    position: relative;
    white-space: nowrap;
    text-overflow: ellipsis;
    justify-content: center;
    cursor: pointer;

    .notch {
        position: absolute;
        height: Pathfinder.$space-50;
        width: Pathfinder.$space-200;
        bottom: -1px;
        border-top-left-radius: Pathfinder.$radius-100;
        border-top-right-radius: Pathfinder.$radius-100;
        background-color: var(--selection);
        left: 50%;
        transform: translateX(-50%);
        z-index: 5;
        opacity: 0;
    }

    &:hover,
    &.hover {
        background: var(--action-glowbox);
        border: 1px solid var(--action);

        .notch {
            opacity: 0;
        }
    }

    &.active {
        background-color: var(--surface-inverse);
        border: 1px solid var(--surface-inverse);
        color: var(--surface-complete);
        white-space: nowrap;
        text-overflow: ellipsis;
        z-index: 6;

        .notch {
            opacity: 1;
        }
    }

    &.sortlist {
        @include Pathfinder.Eyebrow;
        padding: Pathfinder.$space-150;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        display: flex;
        background: transparent;

        .segment-text {
            display: flex;
            justify-content: center;
            // margin-right: -1.44px; // Label will be 1px to the right without countering the letter-spacing of Eybrow...
        }

        &:hover {
            background: var(--action-glowbox);
        }

        &.active {
            @include Pathfinder.Eyebrow-Strong;
            background-color: var(--selection-glowbox);
            border: 1px solid var(--selection-glowbox-border);
            color: var(--on-selection-glowbox);
            white-space: nowrap;
            text-overflow: ellipsis;
            z-index: 6;

            .notch {
                opacity: 1;
            }
        }

        &:hover {
            background: var(--action-glowbox);
            border: 1px solid var(--action-glowbox-border);
            color: var(--on-action-glowbox);

            .notch {
                background-color: var(--on-action-glowbox);
            }
        }
    }

    &.thumbnail {
        @include Pathfinder.Eyebrow;
        display: flex;
        flex-direction: column;
        gap: Pathfinder.$space-150;
        width: 104px;
        border-radius: Pathfinder.$space-200;
        padding: Pathfinder.$space-200;
        overflow: hidden;

        &:hover {
            background: var(--action-glowbox);
            border: 1px solid var(--action);
        }

        .segment-icon {
            height: Pathfinder.$icon-large;
            width: Pathfinder.$icon-large;
        }

        &.active {
            @include Pathfinder.Eyebrow-Strong;
            background-color: var(--selection-glowbox);
            border: 1px solid var(--selection);
            color: var(--on-selection-glowbox);
            white-space: nowrap;
            text-overflow: ellipsis;
            z-index: 6;

            .notch {
                opacity: 0;
            }
        }
    }

    &.thumbnail-tight {
        @include Pathfinder.Toolname;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: Pathfinder.$space-75;
        width: 100%;
        max-width: 96px;
        border-radius: Pathfinder.$space-200;
        padding: Pathfinder.$space-100;
        overflow: hidden;

        &:hover {
            background: var(--action-glowbox);
            border: 1px solid var(--action);
        }

        .segment-text {
            display: flex;
            justify-content: center;
        }

        .segment-icon {
            height: Pathfinder.$icon-large;
            width: Pathfinder.$icon-large;
            border-radius: Pathfinder.$space-75;
            overflow: hidden;

            // required for basemap svgs .. remove height and width on svg?
            ::v-deep(svg) {
                height: Pathfinder.$icon-large;
                width: Pathfinder.$icon-large;
            }
        }

        &.active {
            background-color: var(--selection-glowbox);
            border: 1px solid var(--selection);
            color: var(--on-selection-glowbox);
            white-space: nowrap;
            text-overflow: ellipsis;
            z-index: 6;

            .notch {
                opacity: 0;
            }
        }
    }

    &.tab {
        background: var(--surface-complete);
        border: 1px solid var(--surface-complete);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        &:hover {
            background: var(--surface-complete);
            border: 1px solid var(--surface-complete);
        }

        .segment-icon {
            height: Pathfinder.$icon-default;
            width: Pathfinder.$icon-default;
        }

        &.active {
            background-color: var(--surface-glowbox);
            border: 1px solid var(--surface-glowbox);
        }
    }
}
</style>
